/**
 * `DocState` (BENCH-V2-SPEC §5, Tier B): the document-scale value of every measured channel, held as per-paragraph state
 * and re-based incrementally. An accepted edit is `apply`'d from a `RangeMeasurement`: the zone's per-paragraph k, e and
 * side data are replaced for the event channels, and the edited paragraphs' facts are replaced for the recurrence and
 * distributional channels. Values are computed with the FROZEN overdispersion φ and joint segmentation of the baseline, so a
 * value moves only because the text moved.
 *
 * Channel families:
 * - rate and proportion channels: ΣK, ΣE through `fitRate` with the frozen φ (exceptions below);
 * - LX-01: K from the per-paragraph tell weights plus the document's adverb excess recomputed from the adverb counts;
 * - PROC-05: the two sub-fits with their frozen sub-φ, composed as the channel does;
 * - MT-01: the bilingual and quote-parity events recomputed from the per-paragraph counts, then the C-MT01-CAP rule;
 * - NA-01 (kinds gate) and OB-01 (prompt residue) keep their frozen flags;
 * - SR-01, ST-03, PROC-01, PROC-08: Kaplan–Meier curves over the global word-hash keys, rescanned exactly;
 * - PROC-06: bimodality over the per-paragraph sentence lengths (frozen φ) and drift over the frozen units;
 * - RH-01, RH-02, RH-03, PT-02: pooled within the frozen joint segments from per-paragraph sufficient statistics.
 */
import { fitRate } from '../channels/framework.ts';
import {
	type DispAcc,
	type DispItems,
	accumulate,
	pooledCv,
	pooledMasd,
	sentenceItems,
	singleSegment,
} from '../channels/helpers.ts';
import { ALL_CHANNELS } from '../channels/index.ts';
import { W as MT_W, mtCap } from '../channels/mt01.ts';
import { KINDS_CAP } from '../channels/na01.ts';
import { PROMPT_SCORE } from '../channels/ob01.ts';
import { dashDef, italicDef, mean2 } from '../channels/proc05.ts';
import { LONG, SHORT, proc06Fit } from '../channels/proc06.ts';
import { PT02_MARKS, pt02Value } from '../channels/pt02.ts';
import type { ChannelDef, ChannelId, ChannelImpl } from '../channels/types.ts';
import type { Doc } from '../document/types.ts';
import { ramp } from '../util/stats.ts';
import type { Baseline } from './baseline.ts';
import { type ParaFacts, paragraphFacts } from './facts.ts';
import type { FrozenContext } from './frozen.ts';
import { RecurrenceScan, type StreamView } from './incremental.ts';
import type { RangeMeasurement } from './range.ts';
import {
	PROC01_FAMILIES,
	PROC08_FAMILIES,
	type RecurrenceValue,
	SR01_FAMILY,
	buildStream,
	proc01FromScans,
	proc01Value,
	proc08FromScans,
	proc08Value,
	sectionStartParas,
	sr01FromScan,
	sr01Value,
	st03Value,
	wordStream,
} from './recur.ts';

/** The document value of one channel (the fields the ledger and the unit outcomes read). */
export interface ChannelDocValue {
	status: 'scored' | 'abstain';
	reason?: string;
	K: number;
	E: number;
	/** The channel's document estimate in its own units, and its display score. */
	estimate: number;
	score: number;
	features: Record<string, number>;
}

const IMPLS = new Map<ChannelId, ChannelImpl>(ALL_CHANNELS.map((i) => [i.def.id, i]));

/** Channels whose value is a Kaplan–Meier statistic over global keys. */
export const RECURRENCE_CHANNELS: readonly ChannelId[] = ['SR-01', 'ST-03', 'PROC-01', 'PROC-08'];
/** Channels pooled within the frozen joint segments or built from sentence lengths. */
export const DISTRIBUTIONAL_CHANNELS: readonly ChannelId[] = [
	'PROC-06',
	'RH-01',
	'RH-02',
	'RH-03',
	'PT-02',
];

/** Event channels (rate and proportion): those with an event scan, minus the recurrence channels. */
export const EVENT_CHANNELS: readonly ChannelId[] = ALL_CHANNELS.filter(
	(i) => i.measure && !RECURRENCE_CHANNELS.includes(i.def.id)
).map((i) => i.def.id);

/** Every channel `DocState` can value. SG-01 and PROC-02 (structural, N route) are left to the Tier C re-analysis. */
export const DOCSTATE_CHANNELS: readonly ChannelId[] = [
	...EVENT_CHANNELS,
	...RECURRENCE_CHANNELS,
	...DISTRIBUTIONAL_CHANNELS,
];

interface EventArrays {
	k: Float64Array;
	e: Float64Array;
	aux: Record<string, number | Float64Array>;
}

const abstained = (reason: string): ChannelDocValue => ({
	status: 'abstain',
	reason,
	K: 0,
	E: 0,
	estimate: 0,
	score: 0,
	features: {},
});

const sum = (a: Float64Array): number => {
	let s = 0;
	for (let i = 0; i < a.length; i++) s += a[i] as number;
	return s;
};

export class DocState {
	readonly fc: FrozenContext;
	readonly paragraphCount: number;
	private readonly events = new Map<ChannelId, EventArrays>();
	private facts: ParaFacts[];
	private readonly secPara: Int32Array;
	private cache = new Map<ChannelId, ChannelDocValue>();
	private recCache = new Map<ChannelId, RecurrenceValue>();
	/** The incremental scans of SR-01, PROC-01 (6-gram, 3-gram) and PROC-08 (three slots), once first needed. */
	private stream: StreamView | undefined;
	private readonly scans = new Map<ChannelId, RecurrenceScan[]>();

	private constructor(
		fc: FrozenContext,
		facts: ParaFacts[],
		events: Map<ChannelId, EventArrays>,
		secPara: Int32Array
	) {
		this.fc = fc;
		this.paragraphCount = fc.paragraphCount;
		this.facts = facts;
		this.secPara = secPara;
		for (const [id, a] of events) this.events.set(id, a);
	}

	/** The state of the unedited baseline: the frozen context's per-paragraph arrays and the facts of every paragraph. */
	static fromBaseline(base: Baseline): DocState {
		const { fc, core } = base;
		const events = new Map<ChannelId, EventArrays>();
		for (const id of EVENT_CHANNELS) {
			const f = fc.channels[id];
			if (f?.k && f.e) {
				const aux: Record<string, number | Float64Array> = {};
				for (const [key, v] of Object.entries(f.aux ?? {}))
					aux[key] = typeof v === 'number' ? v : v.slice();
				const k = f.k.slice();
				// MT-01's two document-level events are recomputed from the counts: take them out of the baseline series
				const docLevel = aux.kDocLevel;
				if (id === 'MT-01' && docLevel instanceof Float64Array)
					for (let p = 0; p < k.length; p++) k[p] = (k[p] as number) - (docLevel[p] as number);
				// FM-01: take out the imperative term and undo the final scale, both recomputed from the counts
				if (id === 'FM-01' && docLevel instanceof Float64Array) {
					const scale = typeof aux.scale === 'number' ? aux.scale : 1;
					for (let p = 0; p < k.length; p++)
						k[p] = (k[p] as number) / scale - (docLevel[p] as number);
				}
				events.set(id, { k, e: f.e.slice(), aux });
			} else {
				// the channel abstained at baseline (no per-paragraph series was kept): scan the baseline Doc once
				events.set(id, scanDocEvents(core.doc, fc, id));
			}
		}
		const secPara = sectionStartParas(fc.paragraphCount, fc.sections);
		return new DocState(fc, paragraphFacts(core.doc), events, secPara);
	}

	/**
	 * The state of a whole Doc built under the frozen context (the from-scratch frozen recompute): every event channel
	 * scanned over the whole Doc with the frozen overrides. The reference `apply` is tested against (T-BV2-MEAS-03).
	 */
	static fromDoc(fc: FrozenContext, doc: Doc): DocState {
		const events = new Map<ChannelId, EventArrays>();
		for (const id of EVENT_CHANNELS) events.set(id, scanDocEvents(doc, fc, id));
		const secPara = sectionStartParas(fc.paragraphCount, fc.sections);
		return new DocState(fc, paragraphFacts(doc), events, secPara);
	}

	clone(): DocState {
		const events = new Map<ChannelId, EventArrays>();
		for (const [id, a] of this.events) {
			const aux: Record<string, number | Float64Array> = {};
			for (const [key, v] of Object.entries(a.aux))
				aux[key] = typeof v === 'number' ? v : v.slice();
			events.set(id, { k: a.k.slice(), e: a.e.slice(), aux });
		}
		const c = new DocState(this.fc, this.facts.slice(), events, this.secPara);
		c.stream = this.stream;
		for (const [id, list] of this.scans)
			c.scans.set(
				id,
				list.map((x) => x.clone())
			);
		return c;
	}

	/**
	 * The checkpoint recompute (BENCH-V2-SPEC §5.7): drops every incremental structure; the next values are rescanned from the
	 * facts. Equal to the incremental values by construction (T-BV2-MEAS-03); a full rescan at 500k words is under 1.5 s.
	 */
	recomputeExact(): void {
		this.stream = undefined;
		this.scans.clear();
		this.cache.clear();
		this.recCache.clear();
	}

	/** Per-paragraph weighted events and exposure of an event channel (current state). */
	perParagraph(id: ChannelId): { k: Float64Array; e: Float64Array } | undefined {
		const a = this.events.get(id);
		if (a) return { k: a.k, e: a.e };
		const r = this.exactRecurrence(id);
		return r?.k && r.e ? { k: r.k, e: r.e } : undefined;
	}

	/**
	 * Re-bases the state on a measured range (an accepted edit): the zone's event arrays and side data, and the facts of the
	 * edited paragraphs. Requires that `m` was measured on the state's text with this state's frozen context.
	 */
	apply(m: RangeMeasurement): void {
		const [za] = m.zone;
		for (const [id, r] of Object.entries(m.channels) as Array<
			[ChannelId, NonNullable<RangeMeasurement['channels'][ChannelId]>]
		>) {
			const a = this.events.get(id);
			if (!a || r.abstained) continue;
			a.k.set(r.k, za);
			a.e.set(r.e, za);
			for (const [key, v] of Object.entries(r.aux ?? {}))
				if (typeof v !== 'number') (a.aux[key] as Float64Array).set(v, za);
		}
		const [ra, rb] = m.range;
		const before = this.stream;
		m.facts.forEach((f, i) => {
			this.facts[ra + i] = f;
		});
		if (before) {
			// re-base the incremental scans on the words [ra', rb') → [ra', rb'') of the edited paragraphs
			const next = buildStream(this.facts);
			const w0 = before.paraFirst[ra] as number;
			const wOld = before.paraFirst[rb] as number;
			const wNew = next.paraFirst[rb] as number;
			for (const list of this.scans.values())
				for (const sc of list) sc.update(next, this.secPara, w0, wOld, wNew);
			this.stream = next;
		}
		this.cache.clear();
		this.recCache.clear();
	}

	/* ---------------- facts of one paragraph (the risk sheet, BENCH-V3-PLAN §5) ---------------- */

	/** Word counts of the sentences of paragraph `p`, in order: the inputs of RH-01, RH-02 and PROC-06. */
	sentenceLengths(p: number): ArrayLike<number> {
		return (this.facts[p] as ParaFacts).sentLen;
	}

	/** Counts of each PT-02 mark in paragraph `p` (`PT02_MARKS` of them, in the channel's order). */
	markCounts(p: number): ArrayLike<number> {
		return (this.facts[p] as ParaFacts).marks;
	}

	/** Prose words of paragraph `p`. */
	wordCount(p: number): number {
		return (this.facts[p] as ParaFacts).wordHash.length;
	}

	/* ---------------- values ---------------- */

	value(id: ChannelId): ChannelDocValue {
		let v = this.cache.get(id);
		if (!v) {
			v = this.compute(id);
			this.cache.set(id, v);
		}
		return v;
	}

	values(
		ids: readonly ChannelId[] = DOCSTATE_CHANNELS
	): Partial<Record<ChannelId, ChannelDocValue>> {
		const out: Partial<Record<ChannelId, ChannelDocValue>> = {};
		for (const id of ids) out[id] = this.value(id);
		return out;
	}

	private def(id: ChannelId): ChannelDef | undefined {
		return this.fc.channels[id]?.def ?? IMPLS.get(id)?.def;
	}

	private gate(def: ChannelDef): string | undefined {
		if (def.lang === 'native' && this.fc.flags.languageUnsupported)
			return `not available for ${this.fc.language.code}`;
		return undefined;
	}

	private compute(id: ChannelId): ChannelDocValue {
		const def = this.def(id);
		if (!def) return abstained('unknown channel');
		const g = this.gate(def);
		if (g) return abstained(g);
		if (RECURRENCE_CHANNELS.includes(id)) {
			const r = this.recurrenceValue(id);
			return {
				status: r.status,
				...(r.reason ? { reason: r.reason } : {}),
				K: r.K,
				E: r.E,
				estimate: r.estimate,
				score: r.score,
				features: r.features,
			};
		}
		if (id === 'PROC-06') return this.proc06(def);
		if (id === 'RH-01' || id === 'RH-02' || id === 'RH-03') return this.rhythm(id, def);
		if (id === 'PT-02') return this.pt02(def);
		return this.eventValue(id, def);
	}

	/* ---- recurrence ---- */

	/** The incremental scans of a channel, built on first use from the current facts. */
	private scansOf(id: 'SR-01' | 'PROC-01' | 'PROC-08'): { ws: StreamView; list: RecurrenceScan[] } {
		if (!this.stream) this.stream = buildStream(this.facts);
		const ws = this.stream;
		let list = this.scans.get(id);
		if (!list) {
			const specs =
				id === 'SR-01'
					? [SR01_FAMILY]
					: id === 'PROC-01'
						? [PROC01_FAMILIES.six, PROC01_FAMILIES.three]
						: PROC08_FAMILIES;
			list = specs.map((spec) => RecurrenceScan.build(spec, ws, this.secPara));
			this.scans.set(id, list);
		}
		return { ws, list };
	}

	/** The value of a recurrence channel: incremental (SR-01, PROC-01, PROC-08) or, for the cheap ST-03, exact. */
	private recurrenceValue(id: ChannelId): RecurrenceValue {
		if (id === 'ST-03') return this.exactRecurrence(id) as RecurrenceValue;
		const { ws, list } = this.scansOf(id as 'SR-01' | 'PROC-01' | 'PROC-08');
		if (id === 'SR-01') return sr01FromScan(list[0] as RecurrenceScan, ws.n);
		if (id === 'PROC-01')
			return proc01FromScans(list[0] as RecurrenceScan, list[1] as RecurrenceScan, ws.n);
		return proc08FromScans(list, ws.n);
	}

	/** Full rescan from the facts, with the per-paragraph series: the reference of the incremental values. */
	private exactRecurrence(id: ChannelId): RecurrenceValue | undefined {
		if (!RECURRENCE_CHANNELS.includes(id)) return undefined;
		let r = this.recCache.get(id);
		if (!r) {
			const P = this.paragraphCount;
			if (id === 'ST-03') r = st03Value(this.facts, this.secPara);
			else {
				const ws = wordStream(this.facts, this.secPara);
				r =
					id === 'SR-01'
						? sr01Value(ws, P)
						: id === 'PROC-01'
							? proc01Value(ws, P)
							: proc08Value(ws, P);
			}
			this.recCache.set(id, r);
		}
		return r;
	}

	/* ---- event channels ---- */

	private phi(id: ChannelId, def: ChannelDef): number {
		const f = this.fc.channels[id];
		return f && f.status === 'scored' ? f.phi : def.prior.phi0;
	}

	private eventValue(id: ChannelId, def: ChannelDef): ChannelDocValue {
		const a = this.events.get(id);
		if (!a) return abstained('no per-paragraph series');
		const E = sum(a.e);
		let K = sum(a.k);
		const features: Record<string, number> = {};
		let events = 0;
		if (id === 'LX-01') {
			// K = Σ tell weights + the adverb excess over the human base rate, spread over the adverbs (a document rate)
			K =
				sum(a.aux.kTell as Float64Array) + Math.max(0, sum(a.aux.nAdv as Float64Array) - 0.02 * E);
		} else if (id === 'MT-01') {
			const other = sum(a.aux.nOther as Float64Array);
			const singles = sum(a.aux.nSingle as Float64Array);
			const doubles = sum(a.aux.nDouble as Float64Array);
			let bilingual = 0;
			let parity = 0;
			if (other > 8 && E > 80) bilingual = 1;
			if (singles % 2 === 1) parity++;
			if (doubles % 2 === 1) parity++;
			K += MT_W.bilingual * bilingual + MT_W.parity * parity;
			events = sum(a.aux.nEvents as Float64Array) + bilingual + parity;
			features.events = events;
		}
		if (id === 'FM-01') {
			// imperative weight 0.6·L/S from the document's lines and prose sentences, then K is capped at E (composite ≤ 1)
			const sc = sum(a.aux.nSent as Float64Array);
			K += sc > 0 ? (0.6 * E * sum(a.aux.nImper as Float64Array)) / sc : 0;
			K = Math.min(K, E);
		}
		if (E < def.minExposure)
			return abstained(`insufficient text: ${E} ${def.exposureUnit} < ${def.minExposure}`);
		const fit = fitRate(def, K, E, this.phi(id, def), def.estimator === 'proportion');
		let score = fit.score;
		if (id === 'PROC-05') {
			const Kd = sum(a.aux.kDash as Float64Array);
			const Ki = sum(a.aux.kItalic as Float64Array);
			const aux = this.fc.channels['PROC-05']?.aux;
			const phiDash = (aux?.phiDash as number | undefined) ?? 1.2;
			const phiItalic = (aux?.phiItalic as number | undefined) ?? 1.2;
			const observable = this.fc.observability.emphasis !== 'none';
			const fitD = fitRate(dashDef, Kd, E, phiDash);
			const fitI = observable ? fitRate(italicDef, Ki, E, phiItalic) : undefined;
			score = fitI ? mean2(fitD.score, fitI.score) : fitD.score;
			features.dashScore = fitD.score;
			if (fitI) features.italicScore = fitI.score;
		} else if (id === 'MT-01') {
			score = mtCap(score, events, E);
		} else if (id === 'NA-01') {
			const kinds = this.fc.channels['NA-01']?.features.kinds ?? 0;
			if (kinds < 2) score = Math.min(KINDS_CAP, score);
		} else if (id === 'OB-01') {
			if (this.fc.channels['OB-01']?.features.promptResidue === 1)
				score = Math.max(score, PROMPT_SCORE);
		}
		return { status: 'scored', K, E, estimate: fit.estimate, score, features };
	}

	/* ---- PROC-06 ---- */

	private proc06(def: ChannelDef): ChannelDocValue {
		const P = this.paragraphCount;
		const k = new Float64Array(P);
		const e = new Float64Array(P);
		const nPara = new Float64Array(P);
		const wPara = new Float64Array(P);
		let N = 0;
		let nShort = 0;
		let nLong = 0;
		for (let p = 0; p < P; p++) {
			const len = (this.facts[p] as ParaFacts).sentLen;
			for (let i = 0; i < len.length; i++) {
				const w = len[i] as number;
				N++;
				e[p] = (e[p] as number) + 1;
				nPara[p] = (nPara[p] as number) + 1;
				wPara[p] = (wPara[p] as number) + w;
				if (w <= SHORT || w >= LONG) {
					k[p] = (k[p] as number) + 1;
					if (w <= SHORT) nShort++;
					else nLong++;
				}
			}
		}
		if (N < def.minExposure)
			return abstained(`insufficient text: ${N} sentences < ${def.minExposure}`);
		const f = proc06Fit(
			{ k, e, nPara, wPara, N, nShort, nLong },
			this.fc.chapters,
			this.fc.scenes,
			this.phi('PROC-06', def)
		);
		return {
			status: 'scored',
			K: f.K,
			E: N,
			estimate: f.fit.estimate,
			score: f.score,
			features: {
				bimodality: f.K / N,
				driftAvailable: f.subB === null ? 0 : 1,
				driftR: f.r ?? 0,
				driftBound: f.bound,
				driftUnits: f.J,
				subScoreBimodality: f.subA,
				subScoreDrift: f.subB ?? 0,
			},
		};
	}

	/**
	 * The value of one frozen joint segment for a segment-valued channel, in the channel's own units: the pooled coefficient
	 * of variation of its sentence lengths (RH-01), the mean absolute successive difference over the mean (RH-02) or the
	 * Shannon entropy of its punctuation marks (PT-02). It is the quantity the window guard (R2.4) watches; the document
	 * value above pools these. `undefined` when the segment has too little to value.
	 */
	segmentValue(id: 'RH-01' | 'RH-02' | 'PT-02', seg: number): number | undefined {
		const { segOf, nSeg } = this.segments();
		if (seg < 0 || seg >= nSeg) return undefined;
		const P = this.paragraphCount;
		if (id === 'PT-02') {
			const counts = new Float64Array(PT02_MARKS);
			for (let p = 0; p < P; p++) {
				if (segOf[p] !== seg) continue;
				const m = (this.facts[p] as ParaFacts).marks;
				for (let i = 0; i < PT02_MARKS; i++) counts[i] = (counts[i] as number) + (m[i] as number);
			}
			const n = counts.reduce((a, b) => a + b, 0);
			if (n < 2) return undefined;
			let h = 0;
			for (const c of counts) if (c > 0) h -= (c / n) * Math.log(c / n);
			return h;
		}
		const len: number[] = [];
		const para: number[] = [];
		for (let p = 0; p < P; p++) {
			if (segOf[p] !== seg) continue;
			for (const w of (this.facts[p] as ParaFacts).sentLen) {
				len.push(w);
				para.push(p);
			}
		}
		if (len.length < 3) return undefined;
		const items = sentenceItems(Float64Array.from(len), Uint32Array.from(para), segOf);
		const acc = accumulate(items, nSeg);
		const v = id === 'RH-02' ? pooledMasd(acc).value : pooledCv(acc).value;
		return Number.isFinite(v) ? v : undefined;
	}

	/* ---- joint-segment channels ---- */

	private segments(): { segOf: Int32Array; nSeg: number } {
		const fc = this.fc;
		if (fc.segments.length > 0) return { segOf: fc.paraSegment, nSeg: fc.segments.length };
		return { segOf: new Int32Array(fc.paragraphCount), nSeg: 1 };
	}

	private rhythm(id: 'RH-01' | 'RH-02' | 'RH-03', def: ChannelDef): ChannelDocValue {
		const { segOf, nSeg: nSeg0 } = this.segments();
		let nSeg = nSeg0;
		let items: DispItems;
		let N: number;
		if (id === 'RH-03') {
			const words: number[] = [];
			const paras: number[] = [];
			for (let p = 0; p < this.paragraphCount; p++) {
				const w = (this.facts[p] as ParaFacts).wordHash.length;
				if (w > 0) {
					words.push(w);
					paras.push(p);
				}
			}
			N = words.length;
			items = {
				count: N,
				n: new Float64Array(N).fill(1),
				s1: Float64Array.from(words),
				s2: Float64Array.from(words, (w) => w * w),
				d: new Float64Array(N),
				c: new Float64Array(N),
				seg: Int32Array.from(paras, (p) => segOf[p] as number),
			};
		} else {
			const len: number[] = [];
			const para: number[] = [];
			for (let p = 0; p < this.paragraphCount; p++)
				for (const w of (this.facts[p] as ParaFacts).sentLen) {
					len.push(w);
					para.push(p);
				}
			N = len.length;
			items = sentenceItems(Float64Array.from(len), Uint32Array.from(para), segOf);
		}
		if (N < def.minExposure)
			return abstained(`insufficient text: ${N} ${def.exposureUnit} < ${def.minExposure}`);
		const stat = (a: DispAcc): { value: number; df: number } => {
			if (id === 'RH-02') {
				const m = pooledMasd(a);
				return { value: m.value, df: m.diffs };
			}
			const c = pooledCv(a);
			return { value: c.value, df: c.df };
		};
		let pooled = stat(accumulate(items, nSeg));
		if (!(pooled.df >= def.minExposure - 1)) {
			items = singleSegment(items);
			nSeg = 1;
			pooled = stat(accumulate(items, 1));
		}
		if (!Number.isFinite(pooled.value)) return abstained('insufficient within-segment replication');
		return {
			status: 'scored',
			K: N,
			E: N,
			estimate: pooled.value,
			score: ramp(pooled.value, def.ramp),
			features: { value: pooled.value, exposure: N, df: pooled.df },
		};
	}

	private pt02(def: ChannelDef): ChannelDocValue {
		const { segOf, nSeg } = this.segments();
		const counts = new Float64Array(nSeg * PT02_MARKS);
		for (let p = 0; p < this.paragraphCount; p++) {
			const m = (this.facts[p] as ParaFacts).marks;
			const base = (segOf[p] as number) * PT02_MARKS;
			for (let i = 0; i < PT02_MARKS; i++)
				counts[base + i] = (counts[base + i] as number) + (m[i] as number);
		}
		const v = pt02Value(counts, nSeg);
		if (!v) return abstained(`insufficient text: fewer than ${def.minExposure} marks`);
		return {
			status: 'scored',
			K: v.total,
			E: v.total,
			estimate: v.value,
			score: ramp(v.value, def.ramp),
			features: { hn: v.value, marks: v.total, buckets: v.buckets },
		};
	}
}

/** Per-paragraph arrays of an event channel on a whole Doc under the frozen context (also: channels that abstained at baseline). */
function scanDocEvents(doc: Doc, fc: FrozenContext, id: ChannelId): EventArrays {
	const impl = IMPLS.get(id);
	const P = fc.paragraphCount;
	if (!impl?.measure) return { k: new Float64Array(P), e: new Float64Array(P), aux: {} };
	const m = impl.measure({ doc, rng: () => 0.5, results: {}, frozen: fc });
	const aux: Record<string, number | Float64Array> = {};
	for (const [key, v] of Object.entries(m.aux ?? {}))
		aux[key] = typeof v === 'number' ? v : v.slice();
	return {
		k: m.abstain ? new Float64Array(P) : m.k.slice(),
		e: m.abstain ? new Float64Array(P) : m.e.slice(),
		aux,
	};
}
